import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import AdminNavigation from '../../components/navigation/AdminNavigation';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import { adminService } from '../../services/adminService';
import { adminUserService } from '../../services/adminUserService';
import { useAuth } from '../../contexts/AuthContext';

const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [whatsappPhone, setWhatsappPhone] = useState('');
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const settings = await adminService?.getSettings();
      setWhatsappPhone(settings?.whatsapp_phone || '+55 21 96717-4817');
    } catch (err) {
      setError(err?.message || 'Erro ao carregar configurações');
    }
  };

  const handleSaveWhatsApp = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await adminService?.updateSetting('whatsapp_phone', whatsappPhone);
      setSuccess('Número do WhatsApp atualizado com sucesso!');
    } catch (err) {
      setError(err?.message || 'Erro ao salvar número do WhatsApp');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e?.preventDefault();
    
    if (passwordForm?.newPassword !== passwordForm?.confirmPassword) {
      setError('As senhas não coincidem');
      return;
    }

    if (passwordForm?.newPassword?.length < 6) {
      setError('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      await adminUserService?.changePassword(passwordForm?.newPassword);
      setSuccess('Senha alterada com sucesso!');
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      setError(err?.message || 'Erro ao alterar senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Configurações - UP MIDIA Admin</title>
        <meta name="description" content="Gerencie configurações do sistema e credenciais" />
      </Helmet>
      <div className="min-h-screen bg-background pb-20">
        <div className="sticky top-0 z-50 bg-background/95 backdrop-blur-md border-b border-border">
          <div className="container mx-auto px-4 py-4">
            <AdminNavigation />
          </div>
        </div>

        <div className="container mx-auto px-4 py-8 max-w-4xl">
          <div className="mb-8">
            <h1 className="text-3xl font-heading font-bold text-foreground mb-2">
              Configurações
            </h1>
            <p className="text-muted-foreground">
              Gerencie configurações do sistema e suas credenciais
            </p>
          </div>

          {error && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500 rounded-lg text-red-500">
              {error}
            </div>
          )}

          {success && (
            <div className="mb-6 p-4 bg-green-500/10 border border-green-500 rounded-lg text-green-500">
              {success}
            </div>
          )}

          <div className="space-y-6">
            {/* WhatsApp Number Settings */}
            <div className="bg-card rounded-lg p-6 glow-primary">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="MessageCircle" size={24} color="var(--color-primary)" />
                <h2 className="text-xl font-heading font-bold text-foreground">
                  Número do WhatsApp
                </h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Número usado para receber solicitações de compra dos clientes
              </p>
              <div className="space-y-4">
                <Input
                  value={whatsappPhone}
                  onChange={(e) => setWhatsappPhone(e?.target?.value)}
                  placeholder="+55 21 96717-4817"
                />
                <Button
                  onClick={handleSaveWhatsApp}
                  disabled={loading}
                  className="glow-primary"
                >
                  {loading ? 'Salvando...' : 'Salvar Número'}
                </Button>
              </div>
            </div>

            {/* Change Password */}
            <div className="bg-card rounded-lg p-6 glow-primary">
              <div className="flex items-center gap-3 mb-4">
                <Icon name="Lock" size={24} color="var(--color-primary)" />
                <h2 className="text-xl font-heading font-bold text-foreground">
                  Alterar Senha
                </h2>
              </div>
              <p className="text-sm text-muted-foreground mb-4">
                Crie uma nova senha segura para sua conta
              </p>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nova Senha
                  </label>
                  <Input
                    type="password"
                    value={passwordForm?.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e?.target?.value }))}
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Confirmar Nova Senha
                  </label>
                  <Input
                    type="password"
                    value={passwordForm?.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e?.target?.value }))}
                    placeholder="Digite a senha novamente"
                    minLength={6}
                    required
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="glow-primary"
                >
                  {loading ? 'Alterando...' : 'Alterar Senha'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSettings;